import React, { useCallback, useEffect, useState } from 'react';
import s3ViewerService from '../services/s3ViewerService';

// Reusable modal S3 file browser — navigate buckets/folders and click a file to pick it.
// Calls onSelect({ bucket, key, name }) then closes. Reuses the admin S3 viewer backend.

const fmtSize = (n) => {
    if (n == null) return '';
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / 1024 / 1024).toFixed(1)} MB`;
};

// Matches "…-decrypted.<ext>" — used by filterDecryptedOnly to show only decrypted files.
const DECRYPTED_RE = /-decrypted(\.[^.]+)$/i;

const overlay = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 };
const panel = { background: '#fff', borderRadius: 10, width: 'min(820px, 94vw)', maxHeight: '85vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' };
const rowStyle = { display: 'flex', alignItems: 'center', borderBottom: '1px solid #f0f0f0' };
const clickRow = { flex: 1, display: 'flex', alignItems: 'center', gap: 8, padding: '9px 12px', background: 'none', border: 'none', textAlign: 'left', cursor: 'pointer', color: '#111827', fontSize: '0.85rem' };
const crumbBtn = { background: 'none', border: 'none', color: '#6d28d9', cursor: 'pointer', fontSize: '0.82rem', padding: 0, fontWeight: 600 };

// Small status pill for the fileStatus() coloring (upsert picker: green = already in index, amber = needs upsert).
const statusPill = (st) => ({
    padding: '1px 8px', borderRadius: 10, fontSize: '0.7rem', fontWeight: 700, whiteSpace: 'nowrap',
    ...(st === 'uploaded' ? { background: '#ecfdf5', color: '#047857' } : { background: '#fffbeb', color: '#b45309' }),
});

// multiSelect: render checkboxes + "select all" + a confirm footer; onSelect receives an ARRAY of
// { bucket, key, name }. Without it the picker keeps its single-file behaviour (click a row to pick one).
export default function S3FilePickerModal({ onSelect, onClose, defaultBucket = null, defaultPrefix = '', filterDecryptedOnly = false, fileStatus = null, multiSelect = false }) {
    const keepFile = (name) => !filterDecryptedOnly || DECRYPTED_RE.test(name || '');
    const [bucket, setBucket] = useState(defaultBucket);
    const [prefix, setPrefix] = useState(defaultBucket ? defaultPrefix : '');
    const [buckets, setBuckets] = useState([]);
    const [folders, setFolders] = useState([]);
    const [files, setFiles] = useState([]);
    const [truncated, setTruncated] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [searchResults, setSearchResults] = useState(null);
    const [selected, setSelected] = useState({}); // multiSelect: key -> { bucket, key, name }

    const toggleSel = (f, name) => setSelected(prev => {
        const n = { ...prev };
        if (n[f.key]) delete n[f.key]; else n[f.key] = { bucket, key: f.key, name: name || f.key };
        return n;
    });

    const loadBuckets = useCallback(async () => {
        setLoading(true); setError(null);
        try { const res = await s3ViewerService.listBuckets(); setBuckets(res.buckets || []); }
        catch (e) { setError(e.response?.data?.message || e.message || 'Failed to list buckets'); }
        finally { setLoading(false); }
    }, []);

    const loadPrefix = useCallback(async (b, p) => {
        setLoading(true); setError(null);
        try {
            const res = await s3ViewerService.list(b, p);
            setFolders(res.folders || []); setFiles(res.files || []); setTruncated(!!res.truncated);
        } catch (e) { setError(e.response?.data?.message || e.message || 'Failed to list objects'); }
        finally { setLoading(false); }
    }, []);

    useEffect(() => {
        setSearchResults(null); setSearchTerm('');
        if (bucket === null) loadBuckets(); else loadPrefix(bucket, prefix);
    }, [bucket, prefix, loadBuckets, loadPrefix]);

    const runSearch = async () => {
        const term = searchTerm.trim();
        if (!bucket || !term) { setSearchResults(null); return; }
        setLoading(true);
        try { const res = await s3ViewerService.search(bucket, term, prefix); setSearchResults(res); }
        catch (e) { setError(e.response?.data?.message || e.message || 'Search failed'); }
        finally { setLoading(false); }
    };

    const pick = (key, name) => onSelect({ bucket, key, name: name || key });

    // The files currently on screen (search results or the folder listing), normalised + filtered.
    const visible = (searchResults
        ? searchResults.files.map(f => ({ key: f.key, size: f.size, name: f.key }))
        : files.map(f => ({ key: f.key, size: f.size, name: f.name }))
    ).filter(f => keepFile(f.name));
    const allSelected = visible.length > 0 && visible.every(f => selected[f.key]);
    const toggleSelectAll = () => setSelected(prev => {
        const n = { ...prev };
        if (allSelected) { for (const f of visible) delete n[f.key]; }
        else { for (const f of visible) n[f.key] = { bucket, key: f.key, name: f.name }; }
        return n;
    });
    const selectedCount = Object.keys(selected).length;

    // One file row — a checkbox toggle in multiSelect mode, a click-to-pick button otherwise.
    const fileRow = (f, name) => {
        const st = fileStatus ? fileStatus(f) : null;
        // Only flag files already in the index; "needs upsert" is implied for the rest.
        const pill = st === 'uploaded' ? <span style={statusPill('uploaded')}>✓ in index</span> : null;
        const size = <span style={{ color: '#9ca3af', fontSize: '0.78rem' }}>{fmtSize(f.size)}</span>;
        if (multiSelect) {
            return (
                <div key={f.key} style={rowStyle}>
                    <label style={{ ...clickRow, cursor: 'pointer' }}>
                        <input type="checkbox" checked={!!selected[f.key]} onChange={() => toggleSel(f, name)} />
                        📄 <span style={{ flex: 1, wordBreak: 'break-all' }}>{name}</span>{pill}{size}
                    </label>
                </div>
            );
        }
        return (
            <div key={f.key} style={rowStyle}>
                <button style={clickRow} onClick={() => pick(f.key, name)}>
                    📄 <span style={{ flex: 1, wordBreak: 'break-all' }}>{name}</span>{pill}{size}
                </button>
            </div>
        );
    };

    // Breadcrumb
    const crumbs = [];
    if (bucket) {
        crumbs.push({ label: bucket, onClick: () => setPrefix('') });
        let acc = '';
        prefix.split('/').filter(Boolean).forEach(part => { acc += part + '/'; const t = acc; crumbs.push({ label: part, onClick: () => setPrefix(t) }); });
    }

    return (
        <div style={overlay} onClick={onClose}>
            <div style={panel} onClick={e => e.stopPropagation()}>
                <div style={{ padding: '0.85rem 1rem', borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', gap: 10 }}>
                    <strong style={{ flex: 1, fontSize: '0.95rem' }}>
                        {multiSelect ? 'Select files from S3' : 'Select a file from S3'}
                        {bucket && !loading && (
                            <span style={{ fontWeight: 400, color: '#6b7280', marginLeft: 8, fontSize: '0.82rem' }}>
                                · {visible.length} file{visible.length === 1 ? '' : 's'}
                            </span>
                        )}
                    </strong>
                    <button className="btn btn-secondary" onClick={onClose}>Close</button>
                </div>

                {/* Breadcrumb */}
                <div style={{ padding: '0.6rem 1rem', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6, borderBottom: '1px solid #f0f0f0' }}>
                    <button style={crumbBtn} onClick={() => { setBucket(null); setPrefix(''); }}>All Buckets</button>
                    {crumbs.map((c, i) => (
                        <React.Fragment key={i}><span style={{ color: '#9ca3af' }}>/</span><button style={crumbBtn} onClick={c.onClick}>{c.label}</button></React.Fragment>
                    ))}
                </div>

                {/* Search within bucket */}
                {bucket && (
                    <div style={{ padding: '0.6rem 1rem', display: 'flex', gap: 8, borderBottom: '1px solid #f0f0f0' }}>
                        <input
                            className="acp-input"
                            style={{ flex: 1 }}
                            value={searchTerm}
                            onChange={e => setSearchTerm(e.target.value)}
                            onKeyDown={e => e.key === 'Enter' && runSearch()}
                            placeholder={`Search keys under ${prefix || bucket + '/'}…`}
                        />
                        <button className="btn btn-primary" onClick={runSearch} disabled={!searchTerm.trim()}>Search</button>
                        {searchResults && <button className="btn btn-secondary" onClick={() => { setSearchResults(null); setSearchTerm(''); }}>Clear</button>}
                    </div>
                )}

                {error && <div style={{ padding: '0.75rem 1rem', color: '#991b1b' }}>{error}</div>}

                <div style={{ overflow: 'auto', flex: 1 }}>
                    {loading ? (
                        <div style={{ padding: '1rem', color: '#6b7280' }}>Loading…</div>
                    ) : bucket === null ? (
                        buckets.length === 0
                            ? <div style={{ padding: '1rem', color: '#6b7280' }}>No buckets.</div>
                            : buckets.map(b => (
                                <div key={b.name} style={rowStyle}>
                                    <button style={clickRow} onClick={() => { setPrefix(''); setBucket(b.name); }}>
                                        📁 <span style={{ fontWeight: 600 }}>{b.name}</span>
                                    </button>
                                </div>
                            ))
                    ) : searchResults ? (
                        visible.length === 0
                            ? <div style={{ padding: '1rem', color: '#6b7280' }}>No matches.</div>
                            : <>
                                {multiSelect && (
                                    <div style={rowStyle}>
                                        <label style={{ ...clickRow, cursor: 'pointer', fontWeight: 600 }}>
                                            <input type="checkbox" checked={allSelected} onChange={toggleSelectAll} /> Select all ({visible.length})
                                        </label>
                                    </div>
                                )}
                                {visible.map(f => fileRow(f, f.name))}
                              </>
                    ) : (
                        <>
                            {folders.map(f => (
                                <div key={f.prefix} style={rowStyle}>
                                    <button style={clickRow} onClick={() => setPrefix(f.prefix)}>📁 <span>{f.name}/</span></button>
                                </div>
                            ))}
                            {multiSelect && visible.length > 0 && (
                                <div style={rowStyle}>
                                    <label style={{ ...clickRow, cursor: 'pointer', fontWeight: 600 }}>
                                        <input type="checkbox" checked={allSelected} onChange={toggleSelectAll} /> Select all ({visible.length})
                                    </label>
                                </div>
                            )}
                            {visible.map(f => fileRow(f, f.name))}
                            {folders.length === 0 && visible.length === 0 && (
                                <div style={{ padding: '1rem', color: '#6b7280' }}>{filterDecryptedOnly ? 'No decrypted files here.' : 'Empty.'}</div>
                            )}
                            {truncated && <div style={{ padding: '0.5rem 0.75rem', color: '#b45309', fontSize: '0.8rem' }}>Showing the first 1000 items.</div>}
                        </>
                    )}
                </div>

                {multiSelect && (
                    <div style={{ padding: '0.75rem 1rem', borderTop: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', gap: 10 }}>
                        <span style={{ flex: 1, fontSize: '0.85rem', color: '#6b7280' }}>{selectedCount} selected</span>
                        {selectedCount > 0 && <button className="btn btn-secondary" onClick={() => setSelected({})}>Clear</button>}
                        <button className="btn btn-primary" disabled={selectedCount === 0} onClick={() => onSelect(Object.values(selected))}>
                            Use {selectedCount} selected
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}
